import React, { useCallback, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import CollectionsAPI, { Collection, CollectionVisibilityFilter, Item } from '../../../api/CollectionsAPI';
import { Autocomplete } from '../../../components/Autocomplete/Autocomplete';
import ConfirmationPopup from '../../../components/ConfirmationPopup/ConfirmationPopup';
import { Icon } from '../../../components/Icon/Icon';
import IconButton from '../../../components/IconButton/IconButton';
import PrimaryButton from '../../../components/PrimaryButton/PrimaryButton';
import SecondaryButton from '../../../components/SecondaryButton/SecondaryButton';
import FilterPill from '../../../components/FilterPill/FilterPill';
import { useGetCollections } from '../../../hooks/useGetCollections';
import deleteIcon from "../../../assets/images/bin.svg";
import edit from "../../../assets/images/pen.svg";
import add from "../../../assets/images/plus.svg";
import funnelIcon from "../../../assets/images/funnel.svg";
import infoIcon from "../../../assets/images/info.svg";
import CollectionForm from '../../../components/CollectionForm/CollectionForm';
import { Modal } from '../../../components/Modal/Modal';
import { FormType } from '../../../shared/form.type';
import CONFIG from '../../../config/config';
import CollectionItemForm from '../../../components/CollectionItemForm/CollectionItemForm';
import MarketingCollectionItemElement from '../../../components/MarketingCollectionItemElement/MarketingCollectionItemElement';
import { PermissionFlags } from '../../../components/PermissionFlags/PermissionFlags';
import Card from '../../../components/Card/Card';
import SimpleListElement from '../../../components/SimpleListElement/SimpleListElement';
import NoDataCTA from '../../../components/NoDataCTA/NoDataCTA';
import duplicate from '../../../assets/images/copy.svg';
import Tooltip from '../../../components/Tooltip/Tooltip';
import catalogImage from '../../../assets/images/catalog.webp';
import { useNavigate } from 'react-router-dom';
// import sendIcon from '../../../assets/images/paper-airplane.svg';
import shareIcon from '../../../assets/images/share.svg';
import { toastService } from '../../../services/ToastService';
import CataloguesAPI from '../../../api/CataloguesAPI';
import { isValidCatalogId, isValidEmail } from '../../../shared/validation';
import { PulseLoader } from 'react-spinners';
import TagsInput from '../../../components/TagsInput/TagsInput';
import Pill from '../../../components/Pill/Pill';
import Table, { ColumnDefinition } from '../../../components/Table/Table';
import { Star } from 'lucide-react';
import ListElement from '../../../components/ListElement/ListElement';
import ElementActions from '../../../components/ElementActions/ElementActions';
import { isVideoFile } from '../../../shared/fileUtils';
import usePermissionFlag from '../../../hooks/usePermissionFlag';
import {
  CatalogExpandedListDropZone,
  CatalogItemDragPayload,
  CatalogTableRowDropZone,
} from '../../../components/CollectionCatalogDnD/CollectionCatalogDnD';
import CatalogItemsDraggableList from '../../../components/CatalogItemsDraggableList/CatalogItemsDraggableList';

const VISIBILITY_FILTERS: CollectionVisibilityFilter[] = ['public', 'shareableByLink', 'private'];

function visibilityTooltipKey(collection: Collection): string {
  if (collection.public && collection.shareableByLink) {
    return 'admin.collections.details.visibility.tooltip.public_and_link';
  }
  if (collection.public) {
    return 'admin.collections.details.visibility.tooltip.public';
  }
  if (collection.shareableByLink) {
    return 'admin.collections.details.visibility.tooltip.shareable_by_link';
  }
  return 'admin.collections.details.visibility.tooltip.private';
}

const ThumbnailImage: React.FC<{ src: string; alt: string; className?: string; t: any }> = ({ src, alt, className, t }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  
  const fileExtension = src.slice(src.lastIndexOf('.'));
  const baseImageUrl = src.replace(fileExtension, '');
  const thumbnailUrl = `${baseImageUrl}-thumbnail${fileExtension}`;
  const [currentSrc, setCurrentSrc] = useState(thumbnailUrl);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    if (currentSrc === thumbnailUrl) {
      setCurrentSrc(src);
    } else {
      setHasError(true);
      setIsLoading(false);
    }
  };

  if (hasError) {
    return (
      <div className={`${className} bg-gray-100 flex items-center justify-center`}>
        <span className="text-xs text-gray-400">{t("admin.collections.items.image_error")!}</span>
      </div>
    );
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className={`${className} bg-gray-200 flex items-center justify-center animate-pulse`}>
          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      )}
      <img
        src={currentSrc}
        alt={alt}
        className={`${className} ${isLoading ? 'hidden' : 'block'}`}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
};


const EmptyCollection = {
  name: '',
  shortName: '',
  type: CONFIG.DEFAULT_PRODUCT_TYPE,
  featured: false,
  lastUpdateTime: null,
  items: [],
  types: [CONFIG.DEFAULT_PRODUCT_TYPE],
  views: 0,
  description: '',
  public: false,
  shareableByLink: false,
  pricesArePublic: false
};

const EmptyItem = {
  name: '',
  featuredPictureUrl: '',
  type: CONFIG.DEFAULT_PRODUCT_TYPE,
  placeOrder: 0,
  images: [],
  price: 0,
  currency: ''
};

export type CollectionColumn = ColumnDefinition<Collection>;

function MarketingCollectionsSegment() {
  const { t } = useTranslation();
  const [visibilityFilters, setVisibilityFilters] = useState<CollectionVisibilityFilter[]>([]);
  const [pricesArePublicFilter, setPricesArePublicFilter] = useState<boolean | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const {
    isLoadingCollections,
    collections,
    pagination,
    setPage,
    addCollection,
    removeCollection,
    updateCollection,
    setExpandedCollection,
    addItem,
    removeItem,
    updateItem,
    duplicateCollection,
    reorderCollectionItems,
    moveItemToCollection,
    refresh
  } = useGetCollections({
    paginated: true,
    pageSize: 10,
    filters: {
      visibility: visibilityFilters,
      pricesArePublic: pricesArePublicFilter,
    },
  });
  const [action, setAction] = useState<FormType>('READ');
  const [selectedCollection, setSelectedCollection] = useState<Collection | null>(null);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [showAppModal, setShowAppModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [catalogData, setCatalogData] = useState<any>(null);
  const [collectionToInvite, setCollectionToInvite] = useState<Collection | null>(null);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [invitationEmails, setInvitationEmails] = useState<string[]>([]);
  const [isInviting, setIsInviting] = useState(false);
  const [expandedCollections, setExpandedCollections] = useState<Set<number>>(new Set());
  const { hasAccess: canEditCollections } = usePermissionFlag(['update-marketing-collections']);

  const isFiltering = visibilityFilters.length > 0 || pricesArePublicFilter !== null;

  const toggleVisibility = (value: CollectionVisibilityFilter) => {
    setVisibilityFilters((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
    setPage(0);
  };

  const togglePricesFilter = (value: boolean) => {
    setPricesArePublicFilter((prev) => (prev === value ? null : value));
    setPage(0);
  };

  const resetFilters = () => {
    setVisibilityFilters([]);
    setPricesArePublicFilter(null);
    setPage(0);
  };

  const dropItemOntoCollection = useCallback(
    (targetCollection: Collection) => (payload: CatalogItemDragPayload) => {
      if (!canEditCollections) {
        return;
      }
      const sourceCollection = collections.find((c) => c.id === payload.sourceCollectionId);
      const movedItem = sourceCollection?.items?.find((i) => i.id === payload.itemId);
      if (!movedItem) {
        return;
      }
      void moveItemToCollection(movedItem, targetCollection).then(() => {
        const id = targetCollection.id;
        if (id != null) {
          setExpandedCollections((prev) => new Set(prev).add(id));
        }
      });
    },
    [canEditCollections, collections, moveItemToCollection],
  );

  let navigate = useNavigate();

  const redirectToTablet = () => {
    navigate('/tablet');
  };  

  const handleInputChange = async (e: any) => {
    const value = e.target.value;
    setInputValue(value);
    
    if (isValidCatalogId(value)) {
      setIsImporting(true);
      try {
        const catalogResponse = await CataloguesAPI.getCatalog(value);
        setCatalogData(catalogResponse.data);
      } finally {
        setIsImporting(false);
      }
    }
  };

  const actionButton = (action: FormType) => {
    switch (action) {
      case 'CREATE':
        return <span className="flex justify-center pt-5"><PrimaryButton text={t("admin.collections.form.add")!} fn={() => handleNewCollection(selectedCollection!)} /></span>;
      case 'UPDATE':
        return <span className="flex justify-center pt-5"><PrimaryButton text={t("admin.collections.form.update")!} fn={() => handleEditCollection(selectedCollection!)} /></span>;
      default:
        return;
    }
  };

  const actionItemButton = (action: FormType) => {
    if (isLoadingCollections) {
      return <span className="flex justify-center pt-10"><PrimaryButton isDisabled text={"..."} isLoading/></span>
    }
    switch (action) {
      case 'CREATE':
        return <span className="flex justify-center pt-5"><PrimaryButton text={t("admin.collections.items.form.add")!} fn={() => handleNewCollectionItem(selectedItem!)} /></span>;
      case 'UPDATE':
        return <span className="flex justify-center pt-5"><PrimaryButton text={t("admin.collections.items.form.update")!} fn={() => handleEditCollectionItem(selectedItem!)} /></span>;
      default:
        return;
    }
  };

  const handleImport = () => {
    setShowImportModal(true);
  };

  const restartImportState = () => {
    setInputValue("");
    setShowImportModal(false);
    setCatalogData(null);
  };

  const importCatalog = async (uuid: string) => {
    if (catalogData) {
      try {
        setIsImporting(true);
        await CataloguesAPI.import(uuid);
        toastService.showToast(t("admin.collections.form.import.success"));
        refresh();
      } catch (error: any) {
        toastService.showToast(t("toast.errors.catalogues.not_found"), 'error');
      } finally {
        setIsImporting(false);
        restartImportState();
      }
    }
  }

  const handleAddCollection = () => {
    setAction('CREATE');
    setSelectedCollection(EmptyCollection);
  };

  const editCollection = (c: Collection) => {
    setAction('UPDATE');
    setSelectedCollection(c);
  }

  const viewCollection = (c: Collection) => {
    setAction('READ');
    setSelectedCollection(c);
  }

  const handleNewCollection = async (collection: Collection) => {
    await addCollection(collection);
    setSelectedCollection(null);
  };

  const handleEditCollectionItem = async (item: Item) => {
    await updateItem(item);
    setSelectedItem(null);
  };

  const editItem = (item: Item) => {
    setAction('UPDATE');
    setSelectedItem(item);
  }

  const viewItem = (item: Item) => {
    setAction('READ');
    setSelectedItem(item);
  }

  const handleRemoveCollectionItem = async (item: Item) => {
    await removeItem(item);
    setSelectedCollection(null);
  };

  const handleNewCollectionItem = async (item: Item) => {
    await addItem(item);
    setSelectedItem(null);
    setAction('READ');
  };

  const handleEditCollection = async (collection: Collection) => {
    await updateCollection(collection);
    setSelectedCollection(null);
    setAction('READ');
  };

  const handleRemoveCollection = async (collection: Collection) => {
    await removeCollection(collection);
    setSelectedCollection(null);
    setAction('READ');
  };

  const search = async (input: string, resultsSize: number, setResultsFn: any) => {
    try {
      const searchResults = await CollectionsAPI.searchItems(input);
      const items = searchResults.data;
      const filteredItems = items.slice(0, resultsSize);
      setResultsFn(filteredItems);
    } catch (err) {
      console.error('Could not search collections');
    }
  };

  const handleOpenItemForm = (c: Collection, item: Item) => {
    setExpandedCollection(c);
    setSelectedItem(item);
    setAction('CREATE');
  };

  const copyLink = async (catalogId: string, accessible: boolean) => {
    if (!accessible) {
      toastService.showToast(t("admin.collections.share.not_private"), 'error');
      return; 
    }
    try {
      const linkToCopy = `${window.location.protocol}//${window.location.host}/catalogues/${catalogId}`;
      await navigator.clipboard.writeText(linkToCopy);
      toastService.showToast(t("admin.collections.share.success"));
    } catch (error) {
      toastService.showToast(t("admin.collections.share.failure"), 'error');
    }
  }

  const addEmail = (emails: string[]) => {
    const filteredEmails = emails
      .filter((email, index, self) => isValidEmail(email) && self.indexOf(email) === index);
    setInvitationEmails(filteredEmails);
  };

  const restartInvitationState = () => {
    setInvitationEmails([]);
    setShowInvitationModal(false);
    setCollectionToInvite(null);
  };

  // const invite = (collectionToInvite: Collection) => {
  //   if (!collectionToInvite.shareableByLink && !collectionToInvite.public) {
  //     toastService.showToast(t("admin.collections.invite.not_valid"), "error");
  //   } else {
  //     setCollectionToInvite(collectionToInvite);
  //     setShowInvitationModal(true);
  //   }
  // }

  const sendInvite = async (id: number, invitationEmails: string[]) => {
    if (id && invitationEmails?.length > 0) {
      try {
        setIsInviting(true);
        await CollectionsAPI.invite(id, invitationEmails);
        toastService.showToast(t("admin.collections.form.invite.success"));
      } catch (error: any) {
        toastService.showToast(t("admin.collections.form.invite.error"), 'error');
      } finally {
        setIsInviting(false);
        restartInvitationState();
      }
    }
  }

  const toggleCollectionExpansion = (collectionId: number) => {
    setExpandedCollections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(collectionId)) {
        newSet.delete(collectionId);
      } else {
        newSet.add(collectionId);
      }
      return newSet;
    });
  };

  const handleCollectionItemsReorder = async (collection: Collection, reorderedItems: Item[]) => {
    const updatedItems = reorderedItems.map((item, index) => ({
      ...item,
      placeOrder: index
    }));
    
    const updatedCollection = { ...collection, items: updatedItems };
    
    try {
      await reorderCollectionItems(updatedCollection);
    } catch (error) {
      console.error('Error reordering items:', error);
      toastService.showToast('Error reordering items', 'error');
    }
  };

  const collectionColumns: CollectionColumn[] = [
    {
      key: 'name',
      title: '',
      render: (collection) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleCollectionExpansion(collection.id!);
          }}
          className="p-1 hover:bg-gray-100 rounded"
        >
          {expandedCollections.has(collection.id!) ? (
            <span className="text-gray-600">▼</span>
          ) : (
            <span className="text-gray-600">▶</span>
          )}
        </button>
      ),
    },
    {
      key: 'description',
      title: t("admin.collections.details.name")!,
      render: (collection) => (
        <div className="flex items-center space-x-2">
          <Tooltip content={collection.name}>
            <p 
              className="w-50 truncate font-medium cursor-pointer hover:text-blue-600 transition-colors"
              onClick={() => viewCollection(collection)}
            >
              {collection.name}
            </p>
          </Tooltip>
          {collection.featured && (
            <Tooltip content={t("admin.collections.featured.tooltip")!}>
              <Star className="w-4 h-4 text-yellow-500 fill-current" />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'items',
      title: t("admin.collections.details.items")!,
      render: (collection) => (
        <div className="w-16 text-center">
          <span className="text-sm font-medium">{collection.items?.length || 0}</span>
        </div>
      ),
    },
    {
      key: 'public',
      title: t("admin.collections.details.visibility")!,
      render: (collection) => (
        <div className="relative flex items-center justify-center px-4">
          <span className="relative inline-flex items-center">
            {collection.public || collection.shareableByLink ? (
              <span className="text-sm text-green-600">{t("admin.collections.details.visibility.public")!}</span>
            ) : (
              <span className="text-sm text-gray-500">{t("admin.collections.details.visibility.private")!}</span>
            )}
            <span className="absolute left-full top-1/2 ml-1 -translate-y-1/2">
              <Tooltip content={t(visibilityTooltipKey(collection))!}>
                <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
                  <Icon url={infoIcon} cssStyles="w-3 h-3" />
                </span>
              </Tooltip>
            </span>
          </span>
        </div>
      ),
    },
    {
      key: 'pricesArePublic',
      title: t("admin.collections.details.prices")!,
      render: (collection) => (
        <Pill 
          text={collection.pricesArePublic ? t("admin.collections.details.prices.visible")! : t("admin.collections.details.prices.hidden")!}
          color={collection.pricesArePublic ? 'green' : 'blue'}
        />
      ),
    },
  ];

  const renderActions = (collection: Collection, index: number): React.ReactNode => {
    return (
      <div className="flex justify-center space-x-1">
        {/* <PermissionFlags requiredFlags={['update-marketing-collections'] || []}>
          <IconButton onClick={() => invite(collection)}>
            <Tooltip content={(() => {
              const translation = t("global.send");
              return translation === "global.send" ? "Send" : translation;
            })()}>
              <Icon url={sendIcon} cssStyles="w-5" />
            </Tooltip>
          </IconButton>
        </PermissionFlags> */}
        <PermissionFlags requiredFlags={['update-marketing-collections'] || []}>
          <IconButton onClick={() => duplicateCollection(collection.id!)}>
            <Tooltip content={(() => {
              const translation = t("global.duplicate");
              return translation === "global.duplicate" ? "Duplicate" : translation;
            })()}>
              <Icon url={duplicate} cssStyles="w-5" />
            </Tooltip>
          </IconButton>
        </PermissionFlags>
        <PermissionFlags requiredFlags={['update-marketing-collections'] || []}>
          <IconButton onClick={() => copyLink(collection.uuid!, collection.public || collection.shareableByLink)}>
            <Tooltip content={(() => {
              const translation = t("global.share");
              return translation === "global.share" ? "Share" : translation;
            })()}>
              <Icon url={shareIcon} cssStyles="w-5" />
            </Tooltip>
          </IconButton>
        </PermissionFlags>
        <PermissionFlags requiredFlags={['update-marketing-collections'] || []}>
          <IconButton onClick={() => handleOpenItemForm(collection, EmptyItem)}>
            <Tooltip content={(() => {
              const translation = t("global.add");
              return translation === "global.add" ? "Add" : translation;
            })()}>
              <Icon url={add} cssStyles="w-5" />
            </Tooltip>
          </IconButton>
        </PermissionFlags>
        <PermissionFlags requiredFlags={['update-marketing-collections'] || []}>
          <IconButton onClick={() => editCollection(collection)}>
            <Tooltip content={(() => {
              const translation = t("global.edit");
              return translation === "global.edit" ? "Edit" : translation;
            })()}>
              <Icon url={edit} cssStyles="w-5" />
            </Tooltip>
          </IconButton>
        </PermissionFlags>
        <PermissionFlags requiredFlags={['remove-marketing-collections'] || []}>
          <IconButton>
            <ConfirmationPopup onConfirm={() => handleRemoveCollection(collection)} confirmText={(() => {
              const translation = t("global.confirm");
              return translation === "global.confirm" ? "Confirm" : translation;
            })()} title={`${(() => {
              const translation = t("global.remove");
              return translation === "global.remove" ? "Remove" : translation;
            })()}: ${collection.name}`}>
              <Tooltip content={(() => {
                const translation = t("global.remove");
                return translation === "global.remove" ? "Remove" : translation;
              })()}>
                <Icon url={deleteIcon} cssStyles="w-5" />
              </Tooltip>
            </ConfirmationPopup>
          </IconButton>
        </PermissionFlags>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <Card>
        <>
          <div className="container flex flex-wrap space-x-5">
            <Autocomplete
                  resultsSize={5}
                  searchFn={search}
                  onSelect={(item: Item) => { setSelectedItem(item); setAction(canEditCollections ? 'UPDATE' : 'READ'); }}
                  placeholder={(() => {
                    const translation = t("admin.collections.search.placeholder");
                    return translation === "admin.collections.search.placeholder" ? "Search collections..." : translation;
                  })()}
                  isLoading={isLoadingCollections}
                  errorMessage={(() => {
                    const translation = t('global.empty_results');
                    return translation === 'global.empty_results' ? "No results found" : translation;
                  })()}
                  toElementFn={(item: Item) =>
                    <SimpleListElement key={item.id} >
                      <MarketingCollectionItemElement item={item} />
                    </SimpleListElement>
                  }
                />
          </div>
          <div className="container flex flex-wrap items-center gap-3 my-3">
            <PermissionFlags requiredFlags={['create-marketing-collections']}>
              <PrimaryButton
                text={(() => {
                  const translation = t("admin.collections.form.new_collection.button");
                  return translation === "admin.collections.form.new_collection.button" ? "New Collection" : translation;
                })()}
                fn={handleAddCollection}
              />
            </PermissionFlags>
            <PermissionFlags requiredFlags={['create-marketing-collections']}>
              <PrimaryButton
                text={(() => {
                  const translation = t("admin.collections.form.import_collection.button");
                  return translation === "admin.collections.form.import_collection.button" ? "Import Collection" : translation;
                })()}
                fn={handleImport}
              />
            </PermissionFlags>
            <PermissionFlags requiredFlags={['see-contracts']}>
              <PrimaryButton 
                text={(() => {
                  const translation = t("admin.collections.cta.sales");
                  return translation === "admin.collections.cta.sales" ? "Go to Sales" : translation;
                })()} fn={() => navigate('/sales?openForm=true')} 
                actionName="Go to Sales"
              />
            </PermissionFlags>
            <SecondaryButton
              text={
                <span className="inline-flex items-center">
                  {t("filters.button", "Filters")}
                  {isFiltering && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-amber-500" />}
                </span>
              }
              fn={() => setShowFilters(!showFilters)}
              icon={<Icon url={funnelIcon} cssStyles="w-4 h-4" />}
            />
            {isFiltering && (
              <SecondaryButton
                text={t("filters.reset", "Reset") as string}
                fn={resetFilters}
                actionName="Reset catalogue filters"
              />
            )}
          </div>
          {showFilters && (
            <div className="container space-y-3 mt-2 mb-2">
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-700">
                  {t("admin.collections.filters.visibility", "Visibility")}
                </h3>
                <div className="flex flex-wrap gap-1">
                  {VISIBILITY_FILTERS.map((value) => (
                    <FilterPill
                      key={value}
                      text={t(`admin.collections.filters.visibility.${value}`)}
                      isActive={visibilityFilters.includes(value)}
                      fn={() => toggleVisibility(value)}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-700">
                  {t("admin.collections.filters.prices", "Prices visibility")}
                </h3>
                <div className="flex flex-wrap gap-1">
                  <FilterPill
                    text={t("admin.collections.filters.prices.visible", "Visible")}
                    isActive={pricesArePublicFilter === true}
                    fn={() => togglePricesFilter(true)}
                  />
                  <FilterPill
                    text={t("admin.collections.filters.prices.hidden", "Hidden")}
                    isActive={pricesArePublicFilter === false}
                    fn={() => togglePricesFilter(false)}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      </Card>
      <Card>
        <DndProvider backend={HTML5Backend}>
          <div className="container">
            <Table<Collection>
              isLoading={isLoadingCollections}
              data={collections}
              columns={collectionColumns}
              onRowClick={(collection) => {
                setAction('READ');
                setSelectedCollection(collection);
              }}
              emptyListComponent={
                <NoDataCTA
                  headline={(() => {
                    const translation = t("admin.collections.empty.cta.title");
                    return translation === "admin.collections.empty.cta.title" ? "No Collections Found" : translation;
                  })()}
                  subtext={(() => {
                    const translation = t("admin.collections.empty.cta.body");
                    return translation === "admin.collections.empty.cta.body" ? "Create your first collection to get started" : translation;
                  })()}
                  primaryActionText={(() => {
                    const translation = t("admin.collections.form.new_collection.button");
                    return translation === "admin.collections.form.new_collection.button" ? "New Collection" : translation;
                  })()}
                  onPrimaryAction={handleAddCollection}
                />
              }
              isSelectable={false}
              selectedItems={[]}
              compareFunction={(item, selectedItem) => item.id === selectedItem.id}
              renderActions={renderActions}
              wrapRow={(row, collection, index) => (
                <>
                  <CatalogTableRowDropZone
                    targetCollectionId={collection.id!}
                    onDropItem={dropItemOntoCollection(collection)}
                  >
                    {row}
                  </CatalogTableRowDropZone>
                  {expandedCollections.has(collection.id!) && (
                    <tr key={`expanded-${collection.id}`}>
                      <td colSpan={collectionColumns.length + 1} className="bg-gray-50 p-4">
                        <CatalogExpandedListDropZone
                          targetCollectionId={collection.id!}
                          onDropItem={dropItemOntoCollection(collection)}
                          className="space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-gray-700">{t("admin.collections.items.title", { name: collection.name })!}</h4>
                            <PrimaryButton
                              text={t("admin.collections.items.add_button")!}
                              fn={() => handleOpenItemForm(collection, EmptyItem)}
                            />
                          </div>
                          {collection.items && collection.items.length > 0 ? (
                            <div className="max-h-[60vh] overflow-y-auto pr-2">
                              <CatalogItemsDraggableList
                                items={collection.items}
                                sourceCollectionId={collection.id!}
                                disabled={!canEditCollections || isLoadingCollections}
                                dragHandleTitle={t('admin.collections.items.move_drag_tooltip')!}
                                onMovedItem={(reorderedItems: Item[]) =>
                                  handleCollectionItemsReorder(collection, reorderedItems)
                                }
                                toElementFn={(item: Item, index: number) => {
                                  const firstImage = item.media && item.media.length > 0 ? item.media[0] : null;
                                  const isVideo = firstImage && isVideoFile(firstImage);
                                  
                                  return (
                                    <ListElement>
                                      <ElementActions actions={
                                        <>
                                          <IconButton onClick={() => editItem(item)}>
                                            <Icon url={edit} cssStyles="w-4" />
                                          </IconButton>
                                          <IconButton>
                                            <ConfirmationPopup onConfirm={() => handleRemoveCollectionItem(item)} confirmText="Confirm" title={`Remove: ${item.name}`}>
                                              <Icon url={deleteIcon} cssStyles="w-4" />
                                            </ConfirmationPopup>
                                          </IconButton>
                                        </>
                                      }>
                                        <div className="flex items-center space-x-3">
                                          {/* Thumbnail */}
                                          <div className="flex-shrink-0 cursor-pointer" onClick={() => viewItem(item)}>
                                            {firstImage ? (
                                              isVideo ? (
                                                <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-300 transition-colors">
                                                  <span className="text-xs text-gray-600 font-medium">{t("admin.collections.items.video_placeholder")!}</span>
                                                </div>
                                              ) : (
                                                <ThumbnailImage 
                                                  src={firstImage} 
                                                  alt={item.name}
                                                  className="w-12 h-12 object-cover rounded-lg hover:opacity-80 transition-opacity"
                                                  t={t}
                                                />
                                              )
                                            ) : (
                                              <div className="w-12 h-12 bg-gray-200 text-center rounded-lg flex items-center justify-center hover:bg-gray-300 transition-colors">
                                                <span className="text-xs text-gray-600 font-medium">{t("admin.collections.items.no_image")!}</span>
                                              </div>
                                            )}
                                          </div>
                                          
                                          {/* Item Details */}
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-2">
                                              <p 
                                                className="font-medium text-sm truncate cursor-pointer hover:text-blue-600 transition-colors"
                                                onClick={() => viewItem(item)}
                                              >
                                                {item.name}
                                              </p>
                                              {item.placeOrder !== undefined && (
                                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                                  #{item.placeOrder + 1}
                                                </span>
                                              )}
                                            </div>
                                            <div className="flex items-center space-x-4 mt-1">
                                              {(!item.price || item.price === 0) ? (
                                                <span className="text-xs text-gray-500">
                                                  {t("admin.collections.items.no_price")!}
                                                </span>
                                              ) : (
                                                <span className="text-xs text-gray-600">
                                                  {item.price} {item.currency || 'EUR'}
                                                </span>
                                              )}
                                              {item.description && (
                                                <span className="text-xs text-gray-500 truncate max-w-xs">
                                                  {item.description}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </ElementActions>
                                    </ListElement>
                                  );
                                }}
                              />
                            </div>
                          ) : (
                            <div className="text-center py-8">
                              <p className="text-gray-500 text-sm">{t("admin.collections.items.empty.message")!}</p>
                              <p className="text-gray-400 text-xs mt-1">{t("admin.collections.items.empty.hint")!}</p>
                            </div>
                          )}
                        </CatalogExpandedListDropZone>
                      </td>
                    </tr>
                  )}
                </>
              )}
            />
          </div>
          {pagination.totalPages > 1 && (
            <div className="flex justify-center mt-4 items-center gap-2">
              <SecondaryButton
                text={t("customers.pagination.prev", "Prev") as string}
                fn={() => setPage(Math.max(0, pagination.number - 1))}
                isDisabled={pagination.number === 0}
                actionName="Catalogue Pagination Prev"
              />
              <span className="px-3 py-1 mx-1 text-sm text-gray-600">
                {t("global.pagination.page", "Page {{page}} of {{totalPages}}", {
                  page: pagination.number + 1,
                  totalPages: pagination.totalPages,
                })}
              </span>
              <SecondaryButton
                text={t("customers.pagination.next", "Next") as string}
                fn={() => setPage(Math.min(pagination.totalPages - 1, pagination.number + 1))}
                isDisabled={pagination.number >= pagination.totalPages - 1}
                actionName="Catalogue Pagination Next"
              />
            </div>
          )}
        </DndProvider>
      </Card>
      {showAppModal &&
        <Modal modalTitle={t("admin.collections.tablet.form.title")} canClose onClose={() => setShowAppModal(false)}>
          <div className="clearfix">
            <img className="rounded-lg float-right ml-4 mb-4 w-full md:w-1/2 lg:w-1/3" src={catalogImage} alt="Tablet"/>
            <p>
              { t("admin.collections.tablet.form.paragraph1") }
            </p>
            <p className="mt-4">
            { t("admin.collections.tablet.form.paragraph2") }
            </p>
            <p className="mt-4">
            { t("admin.collections.tablet.form.paragraph3") }
            </p>
            <br/>
            <br/>
            <PrimaryButton isDisabled text={t("admin.collections.tablet.form.button")!} fn={redirectToTablet}/>
          </div>
        </Modal>
      }
      {
        showInvitationModal &&
        <Modal modalTitle={t("admin.collections.form.invite.title")!} canClose onClose={restartInvitationState}>
          <div className="space-y-3">
            <div className="flex justify-between flex-wrap bg-gray-50 p-4 rounded-md">
              <div>
                <h3 className="text-lg font-bold">{collectionToInvite?.name}</h3>
                <span className="flex space-x-2">
                  <p>{t("admin.collections.form.invite.prices")!}:</p>
                  <Pill 
                    color={collectionToInvite?.pricesArePublic ? 'green' : 'blue'} 
                    text={collectionToInvite?.pricesArePublic ? 
                      t("admin.collections.form.invite.prices.visible")!
                      : 
                      t("admin.collections.form.invite.prices.hidden")!
                    } 
                  />
                </span>
                <p>{t("admin.collections.form.import.products")}: { collectionToInvite?.items?.length || 0 }</p>
              </div>
              { 
                collectionToInvite?.items && 
                collectionToInvite?.items[0] &&
                collectionToInvite?.items[0].media &&
                collectionToInvite?.items[0].media[0] && 
                <img src={collectionToInvite?.items[0].media[0]} alt="First item of the catalog" className="inline mr-2 w-32 rounded h-auto"/> 
              }
            </div>
            <p>{t("admin.collections.form.invite.emails.instructions")!}.</p>
            <TagsInput label={t("admin.collections.form.invite.emails")!} tags={invitationEmails} onChange={(value) => addEmail(value)} isDisabled={false}/>
            <div className="flex justify-center">
              {isInviting ? 
                <div className="flex justify-center py-10">
                  <PulseLoader color="#fdd1d1" />
                </div> :
                <PrimaryButton fn={() => sendInvite(collectionToInvite?.id!, invitationEmails)} text={t("global.confirm")!} isDisabled={invitationEmails.length <= 0} actionName="Invite Partner" />
              }
            </div>
          </div>
        </Modal>
      }
      {selectedCollection &&
        <Modal modalTitle={selectedCollection?.name || "admin.collections.form.new_collection"} canClose onClose={() => setSelectedCollection(null)}>
          <CollectionForm
            collection={selectedCollection}
            onCollectionForm={setSelectedCollection}
            actionButton={actionButton(action)}
            isDisabled={action === 'READ'}
          />
        </Modal>
      }
      {selectedItem && (
        <Modal modalTitle={selectedItem?.name || "admin.collections.items.form.new_item"} canClose onClose={() => {setSelectedItem(null); setAction('READ');}}>
          <CollectionItemForm
            item={selectedItem}
            onCollectionItemForm={setSelectedItem}
            actionButton={actionItemButton(action)}
            isDisabled={action === 'READ'}
          />
        </Modal>
      )}
      { showImportModal && (
        <Modal modalTitle={"admin.collections.form.new_collection"} canClose onClose={() => { setShowImportModal(false); restartImportState(); }}>
          <div className="flex flex-col space-y-4">
          <div className="flex items-center md:space-x-1 flex-wrap">
            <p className="text-gray-700">https://my.eateliers.com/catalogues/</p>
            <input 
              type="text" 
              className="border border-gray-50 rounded-md p-2 flex-grow"
              placeholder={t("admin.collections.form.import.uuid")!}
              onChange={handleInputChange}
              value={inputValue}
              disabled={isImporting}
            />
          </div>
            {isImporting && (
              <div className="flex justify-center py-10">
                <PulseLoader color="#fdd1d1" />
              </div>
            )}
            {!isImporting && catalogData && (
              <>
                <div className="bg-gray-100 p-4 rounded-md">
                  <h3 className="text-lg font-bold">{catalogData.name}</h3>
                  <p>{t("admin.collections.form.import.designer")}: {catalogData.storeName}</p>
                  <p className="font-bold">{t("admin.collections.form.import.store")}: {catalogData.storeName}</p>
                  <p>{t("admin.collections.form.import.products")}: { catalogData.items?.length || 0 }</p>
                </div>
                <div className="flex justify-center">
                  <PrimaryButton fn={() => importCatalog(catalogData?.uuid!)} text={t("global.confirm")!} actionName="Import Catalog by ID"/>
                </div>
              </>
            )}
          </div>
      </Modal>
      )}
    </div>
  );
}

export default MarketingCollectionsSegment;
